import {
    SET_ENABLE,
    KEY_LIFESCIENCE_ENABLE,
    LIFESCIENCE_ENABLE,
    LIFESCIENCE_DISABLE,
} from '@/constants';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { findKeyFn } from '@/components/bio-highlight/utils/extractSeqs';
import cookie from 'js-cookie';
import { bus } from 'wujie';

const state = {
    enable: (cookie.get('pvChemicalHl') !== 'false')
        ? LIFESCIENCE_DISABLE
        : localStorage.getItem(KEY_LIFESCIENCE_ENABLE) || LIFESCIENCE_DISABLE,
    fromBio: false,
    querySeq: [],
    querySeqName: [],
    querySeqId: [],
    querySelectedSeqsProcessing: true,
    selectedSeqs: [],
    selectedSeqTarget: undefined,
    lastSearchSeq: undefined,
    lastSearchSetting: undefined,
    querySequences: [],
    searchSettingLists: {
        upload: {},
    }, // 临时保存用户配置了的setting
    hitSeqNumsView: [], // 当前展示的命中的高亮序列的序列编号
    /**
     * 是否显示bio panel
     */
    bioPanelVisible: false,
    /**
     * panel数据加载状态
     */
    panelLoading: false,
    /**
     * 上传序列搜索结果
     */
    bioSequenceSearchResult: {},
    /**
     * panel上的tab
     */
    searchMode: 'selected',
    /**
     * 非蓝色，高亮的序列
     * string[]
     */
    highlightKeys: [],
    isUkLoading: true,
};

const mutations = {
    SET_QUERY_SEQ(state, querySeq) {
        state.querySeq = querySeq.map(v => get(v, 'sequence', ''));
        state.querySeqName = querySeq.map(v => get(v, 'search_name', ''));
        state.querySeqId = querySeq.map(v => get(v, 'sequence_id', ''));
        state.querySequences = querySeq;
    },
    SET_QUERY_SEQS_PROCESSING(state, processing) {
        state.querySelectedSeqsProcessing = processing;
    },
    SET_SELECTED_SEQS(state, selectedSeqs) {
        state.selectedSeqs = [];
        selectedSeqs.forEach(i => state.selectedSeqs.push(i));
    },
    SET_SELECTED_TARGET(state, target) {
        state.selectedSeqTarget = target;
    },
    SET_ENABLE(state, bool) {
        state.enable = bool;
    },
    SET_SEARCH_SETTING(state, payload) {
        const {
            key,
            value,
        } = payload;
        state.searchSettingLists[key] = value;
    },
    SET_SEARCH_SETTING_ITEM(state, payload) {
        const {
            settingKey,
            itemKey,
            value,
        } = payload;
        if (state.searchSettingLists[settingKey]) {
            const settings = cloneDeep(state.searchSettingLists);
            settings[settingKey][itemKey] = value;
            state.searchSettingLists = settings;
        }
    },
    SET_LAST_SEARCH_SEQ(state, seq) {
        state.lastSearchSeq = seq;
    },
    SET_LAST_SEARCH_SETTING(state, setting) {
        state.lastSearchSetting = setting;
    },
    SET_HIT_SEQ_NUMS_VIEW(state, nums) {
        state.hitSeqNumsView = nums;
    },
    SET_BIO_PANEL_VISIBLE(state, visible) {
        state.bioPanelVisible = visible;
    },
    SET_BIO_PANEL_Loading(state, loading) {
        state.panelLoading = loading;
    },
    SET_BIO_SEQUENCE_SEARCH_RESULT(state, result) {
        state.bioSequenceSearchResult = result;
    },
    SET_BIO_SEQUENCE_SEARCH_MODE(state, mode) {
        state.searchMode = mode;
    },
    SET_BIO_HIGHLIGHT_KEYS(state, keys) {
        state.highlightKeys = keys;
    },
    SET_UK_LOADING(state, isLoading) {
        state.isUkLoading = isLoading;
    },
};

const getters = {
    highlightEnable: ({ enable }) => {
        return enable === LIFESCIENCE_ENABLE;
    },
    querySequence: ({ querySeq }) => querySeq,
    querySequenceName: ({ querySeqName }) => querySeqName,
    querySequenceId: ({ querySeqId }) => querySeqId,
    querySequences: ({ querySequences }) => querySequences,
    // 返回的是数组，每一项内容: { seqId, sequence, sequenceType }
    selectedSequences: ({ selectedSeqs }) => selectedSeqs,
    selectedTarget: ({ selectedSeqTarget }) => selectedSeqTarget,
    querySequencesProcessing: ({ querySelectedSeqsProcessing }) => querySelectedSeqsProcessing,
    searchSettingLists: ({ searchSettingLists }) => searchSettingLists,
    lastSearchSeq: ({ lastSearchSeq }) => lastSearchSeq,
    lastSearchSetting: ({ lastSearchSetting }) => lastSearchSetting,
    hitSeqNumsView: ({ hitSeqNumsView }) => hitSeqNumsView,
    bioPanelVisible: ({ bioPanelVisible }) => bioPanelVisible,
    panelLoading: ({ panelLoading }) => panelLoading,
    bioSequenceSearchResult: ({ bioSequenceSearchResult }) => bioSequenceSearchResult,
    /**
     * 判断选择的是不是query
     * @param selectedSeqTarget
     * @param querySequences
     * @returns {*}
     */
    isSelectQuery: ({ selectedSeqTarget, querySequences }) => {
        const bioUk = get(selectedSeqTarget, 'bioUk');
        const labelKey = get(selectedSeqTarget, 'labelKey');
        // 生成 query 的序列对应的key, 每个query可能对应多个子序列
        const queryKey = labelKey ? `${bioUk}_${labelKey}` : bioUk;
        const key = queryKey || get(selectedSeqTarget, 'seqId') || '';
        return querySequences.some(x => findKeyFn(x, key));
    },
    panelSearchMode: ({ searchMode }) => searchMode,
    highlightKeys: ({ highlightKeys }) => highlightKeys,
    isUkLoading: ({ isUkLoading }) => isUkLoading,
};

const actions = {
    toggle({
        commit,
        state,
        dispatch,
    }, bool) {
        const enable = bool === undefined ? !state.enable : bool;
        const value = enable ? LIFESCIENCE_ENABLE : LIFESCIENCE_DISABLE;
        localStorage.setItem(KEY_LIFESCIENCE_ENABLE, value);
        if (enable) {
            bus.$emit('bio-assistant:updateHighlightEnabled', true);
        }
        commit(SET_ENABLE, value);
    },
    setQuerySequence({ commit }, seq) {
        commit('SET_QUERY_SEQ', seq);
    },
    setQueryProcessing({ commit }, payload) {
        commit('SET_QUERY_SEQS_PROCESSING', payload);
    },
    setSelectTaregt({ commit }, payload) {
        commit('SET_SELECTED_TARGET', payload);
    },
    selectSequences({ commit }, seqIds) {
        commit('SET_QUERY_SEQS_PROCESSING', true);
        const merged = seqIds.map(item => {
            return {
                seqId: get(item, 'sequence_id', '') || get(item, 'seqId', '') || get(item, 'sequence_number', '') || item,
                seqHashId: get(item, 'sequence_hash_id', '') || get(item, 'seqHashId'),
                sequence: get(item, 'sequence', ''),
                sequenceType: get(item, 'sequence_type', 'PROTEIN'),
            };
        });
        commit('SET_SELECTED_SEQS', merged);
        commit('SET_QUERY_SEQS_PROCESSING', false);
    },
    setSearchSetting({ commit }, payload) {
        commit('SET_SEARCH_SETTING', payload);
    },
    setSearchSettingItem({ commit }, payload) {
        commit('SET_SEARCH_SETTING_ITEM', payload);
    },
    setLastSearchSeq({ commit }, payload) {
        commit('SET_LAST_SEARCH_SEQ', payload);
    },
    setLastSearchSetting({ commit }, payload) {
        commit('SET_LAST_SEARCH_SETTING', payload);
    },
    setHitSeqNumsView({ commit }, payload) {
        commit('SET_HIT_SEQ_NUMS_VIEW', payload);
    },
    setBioPanelVisible({ commit }, visible) {
        commit('SET_BIO_PANEL_VISIBLE', visible);
    },
    setBioPanelLoading({ commit }, loading) {
        commit('SET_BIO_PANEL_Loading', loading);
    },
    setBioSequenceSearchResult({ commit }, payload) {
        commit('SET_BIO_SEQUENCE_SEARCH_RESULT', payload);
    },
    setSearchMode({ commit }, payload) {
        commit('SET_BIO_SEQUENCE_SEARCH_MODE', payload);
    },
    setHighlightKeys({ commit }, payload) {
        commit('SET_BIO_HIGHLIGHT_KEYS', payload);
    },
    setIsUkLoading({ commit }, isLoading) {
        commit('SET_UK_LOADING', isLoading);
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
